import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { FileText, Clock, LogOut, Menu, X, Receipt, Users, Building2, Home, Hammer, CalendarClock, Landmark, Calculator, BookOpen, UserSearch, HardHat, CalendarDays } from 'lucide-react';
import { Button } from '../../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_flats-clean-service/artifacts/2b1a8yim_Screenshot_20260123_012606_Drive.jpg";

const AdminLayout = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Use defined backend URL or default to same origin for production
  const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/admin/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });

      if (response.ok) {
        sessionStorage.setItem('adminAuth', 'true');
        sessionStorage.setItem('adminPassword', password);
        setIsAuthenticated(true);
      } else {
        setError('Invalid password');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    sessionStorage.removeItem('adminPassword');
    setIsAuthenticated(false);
    navigate('/admin');
  };

  const navItems = [
    { path: '/admin', icon: FileText, label: 'Quotes', exact: true },
    { path: '/admin/reminders', icon: Clock, label: 'Follow-ups' },
    { type: 'divider', label: 'Invoicing' },
    { path: '/admin/clients', icon: Users, label: 'Clients' },
    { path: '/admin/contracts', icon: Building2, label: 'Contracts' },
    { path: '/admin/invoices', icon: Receipt, label: 'Invoices' },
    { path: '/admin/oneoff', icon: CalendarClock, label: 'One-Off', highlight: true },
    { path: '/admin/airbnb', icon: Home, label: 'AirBnB', highlight: true },
    { type: 'divider', label: 'Just Renovated' },
    { path: '/admin/renovated-quotes', icon: FileText, label: 'JR Quotes', highlight: 'orange' },
    { path: '/admin/renovated', icon: Hammer, label: 'JR Invoices', highlight: 'orange' },
    { type: 'divider', label: 'Tools' },
    { path: '/admin/outreach', icon: UserSearch, label: 'Outreach Database', highlight: 'teal' },
    { path: '/admin/workers', icon: HardHat, label: 'Workers', highlight: 'orange' },
    { path: '/admin/schedule', icon: CalendarDays, label: 'Schedule', highlight: 'orange' },
    { path: '/admin/bookkeeping', icon: BookOpen, label: 'Bookkeeping', highlight: 'purple' },
    { path: '/admin/accounts-filing', icon: Calculator, label: 'Accounts Filing', highlight: 'green' },
    { path: '/admin/companies-house', icon: Landmark, label: 'Companies House', highlight: 'blue' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex justify-center mb-6">
              <div className="bg-white rounded-xl px-4 py-2.5 border border-gray-100">
                <img src={LOGO_URL} alt="Just Cleaned" className="h-8 w-auto object-contain" />
              </div>
            </div>
            
            <h1 className="text-xl font-semibold text-gray-900 text-center mb-2">Admin Access</h1>
            <p className="text-gray-500 text-sm text-center mb-6">Enter password to continue</p>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:border-[#1a1f4e] focus:ring-2 focus:ring-[#1a1f4e]/10 outline-none transition-colors"
                data-testid="admin-password-input"
              />
              
              {error && (
                <p className="text-red-500 text-sm text-center">{error}</p>
              )}
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#1a1f4e] hover:bg-[#2a2f5e] text-white rounded-xl font-medium"
                data-testid="admin-login-btn"
              >
                {loading ? 'Verifying...' : 'Access Admin'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 -ml-2">
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <img src={LOGO_URL} alt="Just Cleaned" className="h-6 w-auto object-contain" />
        </div>
        <button onClick={handleLogout} className="text-gray-500 hover:text-gray-700">
          <LogOut className="w-5 h-5" />
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-40
          w-64 bg-white border-r border-gray-200
          transform lg:transform-none transition-transform duration-200
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="p-6 border-b border-gray-100 hidden lg:block">
            <img src={LOGO_URL} alt="Just Cleaned" className="h-8 w-auto object-contain" />
            <p className="text-xs text-gray-400 mt-2">Admin Panel</p>
          </div>
          
          <nav className="p-4 space-y-1 mt-16 lg:mt-0 pb-20 lg:pb-20 overflow-y-auto max-h-[calc(100vh-180px)]">
            {navItems.map((item, index) => (
              item.type === 'divider' ? (
                <div key={index} className="pt-4 pb-2">
                  <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">{item.label}</p>
                </div>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors
                    ${isActive(item.path, item.exact) 
                      ? item.highlight === 'orange' 
                        ? 'bg-orange-500 text-white' 
                        : item.highlight === 'blue'
                          ? 'bg-blue-600 text-white'
                          : item.highlight === 'green'
                            ? 'bg-green-600 text-white'
                            : item.highlight === 'purple'
                              ? 'bg-purple-600 text-white'
                              : item.highlight === 'teal'
                                ? 'bg-teal-600 text-white'
                                : item.highlight 
                                  ? 'bg-rose-600 text-white' 
                                  : 'bg-[#1a1f4e] text-white' 
                      : item.highlight === 'orange'
                        ? 'text-orange-600 hover:bg-orange-50 border border-orange-200'
                        : item.highlight === 'blue'
                          ? 'text-blue-600 hover:bg-blue-50 border border-blue-200'
                          : item.highlight === 'green'
                            ? 'text-green-600 hover:bg-green-50 border border-green-200'
                            : item.highlight === 'purple'
                              ? 'text-purple-600 hover:bg-purple-50 border border-purple-200'
                              : item.highlight === 'teal'
                                ? 'text-teal-600 hover:bg-teal-50 border border-teal-200'
                                : item.highlight 
                                  ? 'text-rose-600 hover:bg-rose-50 border border-rose-200' 
                                  : 'text-gray-600 hover:bg-gray-100'
                    }
                  `}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              )
            ))}
          </nav>
          
          <div className="absolute bottom-0 left-0 w-64 p-4 border-t border-gray-100 bg-white hidden lg:block">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100 w-full transition-colors"
              data-testid="sign-out-btn"
            >
              <LogOut className="w-5 h-5" />
              Sign Out
            </button>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/20 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 min-h-screen lg:min-h-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
